// Libs
import { Control, Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Fragment, useEffect, useMemo, useState } from "react";
import QuestionMarkOutlined from "@mui/icons-material/QuestionMarkOutlined";
import { MdInfo } from "react-icons/md";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { Popover, Transition } from "@headlessui/react";

// Local
import BaseInputText from "../../components/BaseInputText";
import BaseInputSelect from "../../components/BaseInputSelect";
import StyledMessage from "../../components/StyledMessage";
import { StyledTooltip } from "../../components/StyledTooltip";
import {
  invoiceFrequency,
  invoiceMethod,
  getReadableInvoiceMethod,
} from "../../models/membership-template";
import { TemplateFormSchema, TemplateFormState } from "./types";
import BaseInputNumber from "../../components/BaseInputNumber";
import BaseInputTextArea from "../../components/BaseInputTextArea";
import { createToastMessageID } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import {
  getInvoiceFrequencyInMonths,
  durationIntervals,
  DurationInterval,
  convertDuration,
} from "../../utils/templateAndMembershipUtils";
import BaseModal from "../../components/BaseModal";
import ButtonColored from "../../components/ButtonColored";
import StyledSwitchGroupWithDescription from "../../components/StyledSwitchGroupWithDescription";

const formDefaultDurations: { duration: string; value: number }[] = [
  { duration: "Ten years", value: 120 } as const,
  { duration: "Five years", value: 60 } as const,
  { duration: "Three years", value: 36 } as const,
  { duration: "Two years", value: 24 } as const,
  { duration: "One year", value: 12 } as const,
  { duration: "Six months", value: 6 } as const,
  { duration: "Indefinite (no expiration)", value: -1 } as const,
  { duration: "Other (custom duration)", value: 0 } as const,
];

interface Props {
  /** this connects the form to the save button in the parent component */
  id: string;
  onSubmit: (formValues: TemplateFormState) => Promise<void>;
  setIsBusy: (isBusy: boolean) => void;
  defaultValues?: TemplateFormState;
  children: {
    pbiSection: React.ReactNode;
    renewalPBISection: React.ReactNode;
    renewalTemplateSection: React.ReactNode;
    taskGenSection: (control: Control<TemplateFormState>) => React.ReactNode;
  };
}

/** These are shared between add/edit membership template components */
export function AddEditTemplateForm(props: Props) {
  const [customDuration, setCustomDuration] = useState<number | null>(null);
  const [chosenInterval, setChosenInterval] = useState<DurationInterval>(
    durationIntervals[0],
  );
  const [dialogStates, setDialogStates] = useState({
    renewalItem: false,
    renewalTemplate: false,
    duration: false,
  });
  const { addToastMessage } = useToastMessageStore();

  const adjustedDefaultValues = useMemo(() => {
    if (!props.defaultValues) {
      // create case --
      return {
        title: "",
        description: "",
        invoiceMethod: invoiceMethod[0],
        invoiceFrequency: invoiceFrequency[0],
        automaticallyGenerateTasks: false,
        automaticallyPayInvoice: false,
        automaticallySendInvoice: false,
        automaticallyRenewMembership: false,
        discount: 0,
        defaultDuration: 12,
      };
    }
    // edit case --
    const defaultDuration = props.defaultValues.defaultDuration;
    // if the defaultDuration is not one of the formDefaultDurations values,
    // we know it is a custom duration
    const found = formDefaultDurations.find(
      (duration) => duration.value === defaultDuration,
    );
    let dd = defaultDuration;
    if (!found) {
      setCustomDuration(defaultDuration);
      dd = 0;
    }
    const dvs: TemplateFormState = {
      ...props.defaultValues,
      defaultDuration: dd,
    };
    return dvs;
  }, [props.defaultValues]);

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    watch,
    setValue,
  } = useForm<TemplateFormState>({
    resolver: zodResolver(TemplateFormSchema),
    defaultValues: adjustedDefaultValues,
  });

  const watchInvMethod = watch("invoiceMethod");
  const watchDefaultDuration = watch("defaultDuration");
  const watchAutoPayToggle = watch("automaticallyPayInvoice");
  const watchAutoRenewToggle = watch("automaticallyRenewMembership");

  const { setIsBusy } = props;
  // update parent component's save button loading state
  useEffect(() => {
    setIsBusy(isSubmitting);
  }, [isSubmitting, setIsBusy]);

  const handleFormSubmit: SubmitHandler<TemplateFormState> = async (
    formValues,
  ) => {
    if (formValues.defaultDuration === 0) {
      if (customDuration == null) {
        addToastMessage({
          id: createToastMessageID(),
          message: "Please enter a custom duration.",
          type: "error",
          dialog: false,
        });
        return;
      }
      // convert to months & round to nearest integer
      if (chosenInterval === "Months") {
        formValues.defaultDuration = Math.round(customDuration);
      } else {
        formValues.defaultDuration = Math.round(customDuration * 12);
      }
      // verify validity
      if (
        isNaN(formValues.defaultDuration) ||
        formValues.defaultDuration === 0
      ) {
        addToastMessage({
          id: createToastMessageID(),
          message: "Please enter a valid duration.",
          type: "error",
          dialog: false,
        });
        return;
      }
    }

    await props.onSubmit(formValues);
  };

  const durationPopover = (
    <Popover className="relative">
      <Popover.Button as="div">
        <ButtonColored kind="infoBlue" overridePadding="p-1" overrideHeight="">
          <MdInfo className="h-4 w-4" />
        </ButtonColored>
      </Popover.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className="absolute left-full z-10 mt-1 w-64 -translate-x-3/4 transform rounded-md border border-gray-300 bg-white px-4 py-2 text-sm shadow-lg xs:left-1/2 xs:-translate-x-1/2">
          <p>
            The duration of a membership is the length of time between its start
            and end dates.
          </p>
        </Popover.Panel>
      </Transition>
    </Popover>
  );

  const itemExampleDialog = (
    <DialogBase
      isOpen={dialogStates.renewalItem}
      onClose={() =>
        setDialogStates((prev) => ({ ...prev, renewalItem: false }))
      }
      headingText="Renewal Item Example"
      dialogMaxWidth="max-w-3xl"
    >
      <ItemExampleContent />
    </DialogBase>
  );

  const templateExampleDialog = (
    <DialogBase
      isOpen={dialogStates.renewalTemplate}
      onClose={() =>
        setDialogStates((prev) => ({ ...prev, renewalTemplate: false }))
      }
      headingText="Renewal Template Example"
      dialogMaxWidth="max-w-screen-sm"
    >
      <TemplateExampleContent />
    </DialogBase>
  );

  return (
    <form
      id={props.id}
      autoComplete="off"
      onSubmit={handleSubmit(handleFormSubmit)}
      className="text-gray-800"
    >
      {itemExampleDialog}
      {templateExampleDialog}

      <h2 className="mb-4 border-b pb-1.5 text-lg font-semibold text-gray-700">
        Basic Information
      </h2>
      <div className="grid grid-cols-1 gap-4 rounded-lg bg-gray-100 px-5 py-4 md:grid-cols-2">
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <div>
              <BaseInputText
                text="Template Name"
                inputName="title"
                admin={true}
                required={true}
                overrideInputBG="bg-gray-100"
                overrideLabelBG="bg-gray-100"
                {...field}
              />
              {errors.title?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.title.message }}
                  </StyledMessage>
                </div>
              )}
            </div>
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <div>
              <BaseInputTextArea
                text="Description"
                inputName="description"
                admin={true}
                required={true}
                rows={1}
                overrideInputBG="bg-gray-100"
                overrideLabelBG="bg-gray-100"
                {...field}
                value={field.value === null ? "" : field.value}
              />
              {errors.description?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.description.message }}
                  </StyledMessage>
                </div>
              )}
            </div>
          )}
        />
      </div>

      {/* SECTION: DURATION */}
      <div className="mb-4 mt-6 flex items-center justify-start gap-4 border-b pb-1.5">
        <h2 className="text-lg font-semibold text-gray-700">
          Membership Duration
        </h2>
        {durationPopover}
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-4 rounded-lg bg-gray-100 px-5 py-4 md:grid-cols-3">
        <div className="space-y-3 text-sm md:col-span-1">
          <p>
            Choose how long memberships created from this template will last.
          </p>
          {props.id === "editTemplateForm" &&
            adjustedDefaultValues.defaultDuration !== -1 && (
              <div className="w-52 rounded-md border border-amber-200 bg-amber-50 p-3">
                <ExclamationTriangleIcon className="float-left -mt-1 mr-1 h-5 w-5 text-amber-800" />
                <p className="text-xs font-medium text-amber-800">
                  Changes only apply to new memberships without end dates
                </p>
              </div>
            )}
          {props.id === "editTemplateForm" &&
            adjustedDefaultValues.defaultDuration === -1 && (
              <div className="w-52 rounded-md border border-amber-200 bg-amber-50 p-3">
                <ExclamationTriangleIcon className="float-left -mt-1 mr-1 h-5 w-5 text-amber-800" />
                <p className="text-xs font-medium text-amber-800">
                  Changing from indefinite will affect existing memberships
                </p>
              </div>
            )}
        </div>

        <div className="md:col-span-2">
          <div className="grid grid-cols-1 gap-3 xs:grid-cols-2">
            <Controller
              name="defaultDuration"
              control={control}
              render={({ field }) => (
                <div className="mt-0.5">
                  <BaseInputSelect
                    inputName="defaultDuration"
                    text="Duration"
                    overrideBG="bg-gray-100"
                    admin={true}
                    required={true}
                    {...field}
                    onChange={(event) => {
                      const numeric = Number(event.target.value);
                      field.onChange(isNaN(numeric) ? 0 : numeric);
                    }}
                  >
                    {formDefaultDurations.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.duration}
                      </option>
                    ))}
                  </BaseInputSelect>
                  {errors.defaultDuration?.message && (
                    <div className="mt-2 text-sm">
                      <StyledMessage type="error">
                        {{ message: errors.defaultDuration.message }}
                      </StyledMessage>
                    </div>
                  )}
                </div>
              )}
            />

            {/* Duration Summary */}
            <div className="flex justify-end">
              {watchDefaultDuration === -1 && (
                <MiniSectionSummary pillText="INDEFINITE">
                  <p>Memberships never expire unless manually set</p>
                </MiniSectionSummary>
              )}

              {watchDefaultDuration === 0 && (
                <MiniSectionSummary pillText="CUSTOM">
                  {customDuration === null || customDuration === 0 ? (
                    <p>Select a duration</p>
                  ) : (
                    <p>
                      Duration: {customDuration} {chosenInterval.toLowerCase()}
                    </p>
                  )}
                </MiniSectionSummary>
              )}

              {watchDefaultDuration !== 0 && watchDefaultDuration !== -1 && (
                <MiniSectionSummary pillText="PRESET">
                  <p>
                    Duration:{" "}
                    {
                      formDefaultDurations.find(
                        (duration) => duration.value === watchDefaultDuration,
                      )?.duration
                    }
                  </p>
                </MiniSectionSummary>
              )}
            </div>
          </div>

          {/* custom duration input */}
          {watchDefaultDuration === 0 && (
            <div className="flex items-center justify-end gap-3 pt-4">
              <BaseInputNumber
                text="#"
                className="max-w-20"
                overrideBG="bg-gray-100"
                inputName="customDuration"
                admin={true}
                required={false}
                value={customDuration ?? ""}
                onChange={(event) => {
                  const numberValue = event.target.valueAsNumber;
                  const valid = !isNaN(numberValue) && numberValue > 0;
                  setCustomDuration(valid ? numberValue : null);
                }}
              />
              <BaseInputSelect
                text="Period"
                inputName="interval"
                overrideBG="bg-gray-100"
                admin={true}
                required={false}
                value={chosenInterval}
                onChange={(event) => {
                  const newInterval = event.target.value as DurationInterval;
                  if (customDuration != null) {
                    const converted = convertDuration(
                      customDuration,
                      chosenInterval,
                      newInterval,
                    );
                    setCustomDuration(Math.round(converted * 100) / 100);
                  }
                  setChosenInterval(newInterval);
                }}
              >
                {durationIntervals.map((interval) => (
                  <option key={interval} value={interval}>
                    {interval}
                  </option>
                ))}
              </BaseInputSelect>
            </div>
          )}
        </div>
      </div>

      {/* SECTION: BILLING/AUTOMATION - invoiceMethod, invoiceFrequency, discount, + auto settings */}
      <h2 className="mb-4 mt-6 border-b pb-2 text-lg font-semibold text-gray-700">
        Billing & Automation
      </h2>
      <div className="grid grid-cols-1 items-start gap-6 rounded-lg bg-gray-100 px-5 py-4 sm:grid-cols-2">
        <div className="space-y-4">
          <Controller
            name="invoiceMethod"
            control={control}
            render={({ field }) => (
              <div>
                <BaseInputSelect
                  inputName="invoiceMethod"
                  text="Billing Method"
                  overrideBG="bg-gray-100"
                  admin={true}
                  required={true}
                  className="max-w-96 capitalize"
                  {...field}
                  value={field.value === null ? "" : field.value}
                  onChange={(event) =>
                    field.onChange(
                      event.target.value === "" ? null : event.target.value,
                    )
                  }
                >
                  {invoiceMethod.map((method) => {
                    return (
                      <option key={method} value={method}>
                        {getReadableInvoiceMethod(method)}
                      </option>
                    );
                  })}
                </BaseInputSelect>
                {errors.invoiceMethod?.message && (
                  <div className="mt-2 text-sm">
                    <StyledMessage type="error">
                      {{ message: errors.invoiceMethod.message }}
                    </StyledMessage>
                  </div>
                )}
              </div>
            )}
          />

          <Controller
            name="invoiceFrequency"
            control={control}
            render={({ field }) => (
              <div>
                <div className="flex items-center gap-2">
                  <div className="max-w-96 flex-1">
                    <BaseInputSelect
                      inputName="invoiceFrequency"
                      text="Billing Frequency"
                      overrideBG="bg-gray-100"
                      admin={true}
                      required={true}
                      className="capitalize"
                      {...field}
                      value={field.value === null ? "" : field.value}
                      onChange={(event) =>
                        field.onChange(
                          event.target.value === "" ? null : event.target.value,
                        )
                      }
                    >
                      {invoiceFrequency.map((frequency) => {
                        return (
                          <option
                            key={frequency}
                            value={frequency}
                            className="capitalize"
                          >
                            {frequency}
                          </option>
                        );
                      })}
                    </BaseInputSelect>
                  </div>
                  <StyledTooltip title="How often should invoices be generated for the membership?">
                    <QuestionMarkOutlined
                      style={{ width: "1.4rem", height: "1.4rem" }}
                      className="rounded-full border border-gray-500 p-0.5 text-gray-500 transition-colors hover:border-gray-700 hover:text-gray-700"
                    />
                  </StyledTooltip>
                </div>
                {errors.invoiceFrequency?.message && (
                  <div className="mt-2 text-sm">
                    <StyledMessage type="error">
                      {{ message: errors.invoiceFrequency.message }}
                    </StyledMessage>
                  </div>
                )}

                {field.value && watchDefaultDuration !== -1 && (
                  <>
                    {getMembershipDurationInMonths(
                      watchDefaultDuration,
                      customDuration,
                      chosenInterval,
                    ) <= getInvoiceFrequencyInMonths(field.value) && (
                      <div className="mt-2 max-w-fit rounded-md border border-blue-300">
                        <StyledMessage
                          type="info"
                          overrideTextAlignment="text-left text-sm"
                          overrideIconAlignment="self-start mt-0.5"
                        >
                          {{
                            message:
                              "No invoices during membership term - only at renewal",
                            icon: <MdInfo />,
                          }}
                        </StyledMessage>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          />

          <Controller
            name="discount"
            control={control}
            render={({ field }) => (
              <div>
                <div className="flex items-center gap-2">
                  <BaseInputNumber
                    text="Member Discount %"
                    className="w-48"
                    inputName="discount"
                    overrideBG="bg-gray-100"
                    admin={true}
                    required={false}
                    {...field}
                    value={field.value}
                    onChange={(event) => {
                      const numberValue = event.target.valueAsNumber;
                      field.onChange(isNaN(numberValue) ? "" : numberValue);
                    }}
                  />
                </div>
                {errors.discount?.message && (
                  <div className="mt-2 text-sm">
                    <StyledMessage type="error">
                      {{ message: errors.discount.message }}
                    </StyledMessage>
                  </div>
                )}
              </div>
            )}
          />
        </div>

        {/* Automation Controls */}
        {watchInvMethod === "automatic" && (
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-medium">
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-1 text-xs text-blue-800">
                AUTO
              </span>
              Automation Settings
            </h3>

            <div className="space-y-3">
              <Controller
                name="automaticallyRenewMembership"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center gap-2">
                    <div className="grow rounded-lg border border-gray-200 bg-white p-3">
                      <StyledSwitchGroupWithDescription
                        title="Auto-Renew"
                        description="Renew at end of term"
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        ref={field.ref}
                        checked={field.value ?? false}
                        id="automaticallyRenewMembership"
                        name={field.name}
                      />
                    </div>
                    <StyledTooltip
                      title={
                        watchAutoRenewToggle
                          ? "Toggle off to prevent the membership from automatically renewing when the end date passes."
                          : "Toggle on to automatically renew the membership when the end date passes."
                      }
                    >
                      <QuestionMarkOutlined
                        style={{ width: "1.4rem", height: "1.4rem" }}
                        className="rounded-full border border-gray-500 p-0.5 text-gray-500 transition-colors hover:border-gray-700 hover:text-gray-700"
                      />
                    </StyledTooltip>
                  </div>
                )}
              />

              <Controller
                name="automaticallyPayInvoice"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center gap-2">
                    <div className="grow rounded-lg border border-gray-200 bg-white p-3">
                      <StyledSwitchGroupWithDescription
                        title="Auto-Charge"
                        description="Charge card automatically"
                        onBlur={field.onBlur}
                        onChange={(ev) => {
                          field.onChange(ev);
                          if (ev) setValue("automaticallySendInvoice", false);
                        }}
                        ref={field.ref}
                        checked={field.value ?? false}
                        id="automaticallyPayInvoice"
                        name={field.name}
                      />
                    </div>
                    <StyledTooltip
                      title={
                        watchAutoPayToggle
                          ? "Toggle off to prevent charging the customer's card when an invoice is generated for the membership."
                          : "Toggle on to automatically charge the customer's card when an invoice is generated for the membership."
                      }
                    >
                      <QuestionMarkOutlined
                        style={{ width: "1.4rem", height: "1.4rem" }}
                        className="rounded-full border border-gray-500 p-0.5 text-gray-500 transition-colors hover:border-gray-700 hover:text-gray-700"
                      />
                    </StyledTooltip>
                  </div>
                )}
              />

              <Controller
                name="automaticallySendInvoice"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center gap-2">
                    <div
                      className={`grow rounded-lg border bg-white p-3 ${
                        watchAutoPayToggle ? "opacity-50" : "border-gray-200"
                      }`}
                    >
                      <StyledSwitchGroupWithDescription
                        disabled={watchAutoPayToggle}
                        title="Auto-Email"
                        description="Email invoices automatically"
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        ref={field.ref}
                        checked={field.value ?? false}
                        id="automaticallySendInvoice"
                        name={field.name}
                      />
                    </div>
                    <StyledTooltip
                      title={getEmailInvTooltip(
                        watchAutoPayToggle,
                        field.value,
                      )}
                    >
                      <QuestionMarkOutlined
                        style={{ width: "1.4rem", height: "1.4rem" }}
                        className="rounded-full border border-gray-500 p-0.5 text-gray-500 transition-colors hover:border-gray-700 hover:text-gray-700"
                      />
                    </StyledTooltip>
                  </div>
                )}
              />
            </div>
          </div>
        )}

        {watchInvMethod === "manual" && (
          <div className="ml-auto">
            <MiniSectionSummary pillText="MANUAL">
              <p>Invoices and renewals must be handled manually</p>
            </MiniSectionSummary>
          </div>
        )}

        {watchInvMethod === "withTask" && (
          <div className="ml-auto">
            <MiniSectionSummary pillText="WITH TASK">
              <p>Invoices managed through task system</p>
            </MiniSectionSummary>
          </div>
        )}
      </div>

      {/* SECTION: PRICING */}
      <h2 className="mb-4 mt-6 border-b pb-2 text-lg font-semibold text-gray-700">
        Pricing
      </h2>
      <div className="grid grid-cols-1 gap-4 rounded-lg bg-gray-100 px-5 py-4 sm:grid-cols-2">
        <div className="space-y-2 text-sm">
          <p>
            Choose the price book item that defines membership fees per billing
            period.
          </p>
          {watchInvMethod === "automatic" && (
            <p>
              This item will be used for all invoices unless renewal pricing is
              specified below.
            </p>
          )}
        </div>
        <div className="sm:w-full sm:place-self-center">
          {props.children.pbiSection}
        </div>
      </div>

      {/* SECTION: RENEWAL */}
      {watchInvMethod === "automatic" && (
        <>
          <h2 className="mb-4 mt-6 border-b pb-2 text-lg font-semibold text-gray-700">
            Renewal Options
          </h2>
          <div className="grid grid-cols-1 gap-4 rounded-lg bg-gray-100 px-5 py-4 sm:grid-cols-2">
            <div className="space-y-2">
              <h3 className="font-medium text-gray-800">
                Different Renewal Price
              </h3>
              <p className="text-sm text-gray-600">
                Select a renewal item if you want to change pricing after the
                initial term.
              </p>
              <ButtonColored
                kind="infoBlue"
                onClick={(ev) => {
                  ev.preventDefault();
                  setDialogStates((prev) => ({
                    ...prev,
                    renewalItem: true,
                  }));
                }}
                overridePadding="px-3 py-1.5"
                overrideHeight=""
              >
                <MdInfo className="mr-1 h-4 w-4" />
                Example
              </ButtonColored>
            </div>
            <div className="sm:w-full sm:place-self-center">
              {props.children.renewalPBISection}
            </div>

            <div className="space-y-2">
              <h3 className="font-medium text-gray-800">
                Different Renewal Template
              </h3>
              <p className="text-sm text-gray-600">
                Select a renewal template for promotional offers that transition
                to standard plans.
              </p>
              <ButtonColored
                kind="infoBlue"
                onClick={(ev) => {
                  ev.preventDefault();
                  setDialogStates((prev) => ({
                    ...prev,
                    renewalTemplate: true,
                  }));
                }}
                overridePadding="px-3 py-1.5"
                overrideHeight=""
              >
                <MdInfo className="mr-1 h-4 w-4" />
                Example
              </ButtonColored>
            </div>
            <div className="sm:w-full sm:place-self-center">
              {props.children.renewalTemplateSection}
            </div>
          </div>
        </>
      )}

      {/* TASKS SECTION */}
      <h2 className="mb-4 mt-6 border-b pb-2 text-lg font-semibold text-gray-700">
        Tasks
      </h2>
      <div className="space-y-4 rounded-lg bg-gray-100 px-5 py-4 text-sm">
        <p>
          Set up tasks that should be completed during the membership period.
          Tasks can be automatically created and scheduled, or managed manually.
        </p>
        {props.children.taskGenSection(control)}
      </div>
    </form>
  );
}

function ItemExampleContent(): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-x-6 gap-y-4 md:flex-row">
      <div className="relative w-full max-w-fit rounded-xl border border-gray-200 bg-white p-4 pb-2 pr-3 md:pb-4 md:pt-5">
        <h3 className="absolute -top-3.5 left-4 bg-white px-4 py-1 text-sm font-medium tracking-wide text-gray-500">
          TEMPLATE
        </h3>
        <div className="space-y-2 text-base">
          <h4 className="font-medium text-gray-600">
            Preventative Maintenance Plan
          </h4>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-gray-600">
            <span>Item Price:</span>
            <span>$100.00</span>
            <span>Renewal Price:</span>
            <span className="font-medium text-blue-700">$85.00</span>
            <span>Frequency:</span>
            <span>Monthly</span>
            <span>Duration:</span>
            <span>1 year</span>
            <span>Auto-Renew:</span>
            <span>Yes</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <p>
          Here's what happens with a membership that starts on January 1, 2025:
        </p>
        <ol className="space-y-2">
          <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-700">
              1
            </span>
            $100.00/month billing for 2025
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-700">
              2
            </span>
            Customer gets renewal reminder (mid-December 2025)
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-700">
              3
            </span>
            Membership automatically renews at the new rate of $85.00/month
            (beginning of January 2026)
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-700">
              4
            </span>
            $85.00/month billing for 2026 and beyond
          </li>
        </ol>
      </div>
    </div>
  );
}

function TemplateExampleContent(): JSX.Element {
  return (
    <div className="text-base leading-tight text-gray-600">
      <div className="flex flex-col items-center gap-x-8 gap-y-4 text-sm xs:flex-row xs:items-start xs:justify-center sm:text-base">
        <div className="relative w-full max-w-fit rounded-xl border border-gray-200 bg-white pb-3 pl-4 pr-3 pt-4 sm:pb-4 sm:pt-5">
          <h3 className="absolute -top-3 left-4 bg-white px-3 py-1 text-xs font-medium tracking-wide text-gray-500 sm:-top-3.5 sm:px-4 sm:text-sm">
            ORIGINAL
          </h3>
          <div className="leading-tight">
            <h4 className="font-medium">Complimentary Maintenance Plan</h4>
            {/* <p className="mb-2 text-sm">Free with equipment installation</p> */}
            <p className="mb-2 text-xs sm:text-sm">
              Free with large equipment install
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <span>Price:</span>
              <span>$0.00</span>
              <span>Frequency:</span>
              <span>Indefinite</span>
              <span>Duration:</span>
              <span>2 years</span>
              <span>Auto-Renew:</span>
              <span>Yes</span>
            </div>
          </div>
        </div>

        <div className="relative -ml-5 w-full max-w-fit rounded-xl border border-gray-200 bg-white pb-3 pl-4 pr-3 pt-4 sm:ml-0 sm:pb-4 sm:pt-5">
          <h3 className="absolute -top-3 left-4 bg-white px-3 py-1 text-xs font-medium tracking-wide text-gray-500 sm:-top-3.5 sm:px-4 sm:text-sm">
            RENEWAL
          </h3>
          <div className="leading-tight">
            <h4 className="mb-2 font-medium">Standard Maintenance Plan</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <span>Item Price:</span>
              <span>$100.00</span>
              <span>Renewal Price:</span>
              <span>$85.00</span>
              <span>Frequency:</span>
              <span>Monthly</span>
              <span>Duration:</span>
              <span>1 year</span>
              <span>Auto-Renew:</span>
              <span>Yes</span>
            </div>
          </div>
        </div>
      </div>

      <p className="mb-2 mt-4">
        Timeline for a membership that starts in January 2025:
      </p>
      <ol className="ml-3 space-y-2">
        <li className="flex items-start gap-3">
          <span className="mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500 opacity-60"></span>
          <span>
            <span className="font-medium">2025, 2026: </span>
            Free period - no invoices
          </span>
        </li>
        <li className="flex items-start gap-3">
          <span className="mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500 opacity-70"></span>
          <span>
            <span className="font-medium">Jan 2027: </span>
            Renews to Standard Maintenance Plan; renewal invoice $100.00
          </span>
        </li>
        <li className="flex items-start gap-3">
          <span className="mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500 opacity-80"></span>
          <span>
            <span className="font-medium">2027: </span>
            $100.00/month billing
          </span>
        </li>
        <li className="flex items-start gap-3">
          <span className="mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500 opacity-90"></span>
          <span>
            <span className="font-medium">Jan 2028: </span>
            Renewal invoice $85.00
          </span>
        </li>
        <li className="flex items-start gap-3">
          <span className="mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500"></span>
          <span>
            <span className="font-medium">2028+: </span>
            $85.00/month billing
          </span>
        </li>
      </ol>
    </div>
  );
}

function DialogBase(props: {
  isOpen: boolean;
  onClose: () => void;
  headingText: string;
  children: React.ReactNode;
  dialogMaxWidth: string;
}): JSX.Element {
  const header = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary px-8 py-6 text-left text-white">
      <h1 className="inline-flex items-center text-xl">{props.headingText}</h1>
      <button type="button" onClick={props.onClose}>
        <XMarkIcon aria-label={`close dialog`} className="h-6 text-white" />
      </button>
    </div>
  );
  return (
    <BaseModal
      open={props.isOpen}
      closeModal={props.onClose}
      title={header}
      parentDivStyles={`text-left ${props.dialogMaxWidth}`}
    >
      <div className="p-6 pt-2">{props.children}</div>
    </BaseModal>
  );
}

/** Calculate the membership duration in months based on the form state */
function getMembershipDurationInMonths(
  defaultDuration: number,
  customDuration: number | null,
  chosenInterval: DurationInterval,
): number {
  if (defaultDuration !== 0) return defaultDuration; // preset duration
  if (customDuration === null) return Infinity;
  return chosenInterval === "Years" ? customDuration * 12 : customDuration;
}

function MiniSectionSummary(props: {
  pillText: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-lg border bg-white px-4 py-2 text-sm text-gray-600">
      <span className="mb-0.5 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800">
        {props.pillText}
      </span>
      {props.children}
    </div>
  );
}

/** get tooltip text for the "email invoice automatically" toggle */
function getEmailInvTooltip(
  autoPay: boolean | undefined,
  autoEmail: boolean | undefined,
): string {
  if (autoPay) {
    return "This setting is disabled because the customer's primary card on file will be charged automatically when a membership invoice is generated.";
  }
  if (autoEmail) {
    return "Toggle off to prevent automatically emailing membership invoices to the customer.";
  }
  return "Toggle on to automatically email membership invoices to the customer.";
}
